import { useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { getAllCrops, getAllFertilizers, getAllTillageSeedings, getCrops, getFertilizers, getModules, getTillageSeedings } from "services";

import { useAdminGeneralContext, useCancelAsyncOnUnmount, useGeneralContext } from "utils";

import { DEFAULT_CACHE_TIME, GET_MODULES } from "consts";

import { Module, ModuleSlug } from "models";

const getFetchDataFunction = (moduleSlug: ModuleSlug, isAdmin: boolean) => {
  if (moduleSlug === "fertilizer") {
    return isAdmin ? getAllFertilizers : getFertilizers;
  }

  if (moduleSlug === "crops") {
    return isAdmin ? getAllCrops : getCrops;
  }

  return isAdmin ? getAllTillageSeedings : getTillageSeedings;
};

const useModuleDataContext = (isAdmin: boolean) => (isAdmin ? useAdminGeneralContext : useGeneralContext);

/**
 * @deprecated: Only to be used for old field module code
 */
export const useModuleData = ({ moduleSlug, onFinish, isAdmin = false }: { moduleSlug?: ModuleSlug; onFinish?: () => void; isAdmin?: boolean }) => {
  const signal = useCancelAsyncOnUnmount()();
  const [loading, setLoading] = useState(true);
  // @ts-expect-error contexts not typed
  const { moduleData, setModuleData } = useModuleDataContext(isAdmin)();

  const handleFinish = useCallback(() => {
    setLoading(false);
    onFinish?.();
  }, [onFinish]);

  const fetchData = useCallback(
    (moduleSlug: ModuleSlug) => {
      getFetchDataFunction(
        moduleSlug,
        isAdmin
      )({
        signal,
        enabled: !isAdmin, // this is required for users to prevent return of disabled elements
      })
        .then((res) => {
          // @ts-expect-error contexts not typed
          setModuleData((prev) => ({
            ...prev,
            [moduleSlug]: res,
          }));
        })
        .finally(handleFinish);
    },
    [signal, setModuleData, isAdmin, handleFinish]
  );

  const handleData = useCallback(() => {
    if (!moduleSlug) {
      handleFinish();
      return;
    }

    setLoading(true);

    if (moduleSlug !== "pesticides") {
      // If module data was already populated in the context
      // we don't need to do it again
      if (moduleData[moduleSlug] !== null) {
        handleFinish();
        return;
      }

      fetchData(moduleSlug);
    } else {
      handleFinish();
    }
  }, [fetchData, handleFinish, moduleData, moduleSlug]);

  useEffect(() => {
    handleData();
  }, [moduleSlug, handleData]);

  return {
    // The admin endpoint returns crops in different format, which we want to take into account here
    crops: isAdmin ? { crops: moduleData.crops } : moduleData.crops || {},
    fertilizers: moduleData.fertilizer || {},
    tillageAndSeedings: moduleData.tillage_seeding || {},
    loading,
  };
};

// this will get and cache the basic module data for 1 day
export const useGetModules = () => {
  const query = useQuery<Module[] | []>({
    queryKey: [GET_MODULES],
    queryFn: () => getModules(),
    gcTime: DEFAULT_CACHE_TIME,
    staleTime: DEFAULT_CACHE_TIME,
  });

  return {
    data: query.data ?? [],
    refetch: query.refetch,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    isSuccess: query.isSuccess,
    isFetched: query.isFetched,
  };
};
